// declarations.js

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import {
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem
} from '@mui/material';

import axios from 'axios';
import apiUrl from '../config';
import DownloadIcon from '@mui/icons-material/Download';
import LayoutDashboard from "../layout/LayoutDashboard";
import "../CSS/Declarations.css";

const getDecodedToken = () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;

    return JSON.parse(atob(token.split('.')[1]));
  } catch {
    return null;
  }
};

const getCacheKey = (userId) => {
  return `declarations_user_cache_${userId}`;
};

const getCachedDeclarations = (userId) => {
  try {
    const cached = sessionStorage.getItem(getCacheKey(userId));
    return cached ? JSON.parse(cached) : [];
  } catch {
    sessionStorage.removeItem(getCacheKey(userId));
    return [];
  }
};

const saveDeclarationsCache = (userId, data) => {
  sessionStorage.setItem(getCacheKey(userId), JSON.stringify(data));
};

const normalizeText = (value) => {
  if (!value) return "";

  return value
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
};

const formatDateValue = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (!isNaN(date.getTime())) {
    return date.toLocaleDateString("fr-FR");
  }

  const parts = value.toString().trim().split("-");

  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }

  return value;
};

const formatDates = (datesString) => {
  if (!datesString || datesString === "0") return "Aucun";

  return datesString
    .split('/')
    .map(date => {
      const cleanDate = date.trim();
      const [year, month, day] = cleanDate.split('-');

      if (!year || !month || !day) return cleanDate;

      return `${day}/${month}/${year}`;
    })
    .join(", ");
};

const getFirstDateFromDeclaration = (declaration) => {
  const source =
    declaration.Jours_Selectionnes ||
    declaration.jours_congé ||
    declaration.jours_maladie ||
    declaration.Date ||
    "";

  if (!source) return null;

  const firstDate = source.toString().split('/')[0]?.trim();

  if (!firstDate || firstDate === "0") return null;

  const parsed = new Date(firstDate);

  return isNaN(parsed.getTime()) ? null : parsed;
};

const getMonthName = (declaration) => {
  const date = getFirstDateFromDeclaration(declaration);

  if (!date) return "Non spécifié";

  return date.toLocaleString("fr-FR", {
    month: "long",
    year: "numeric"
  });
};

const sortDeclarationsRecentFirst = (list) => {
  return [...list].sort((a, b) => {
    const getDeclarationDateValue = (declaration) => {
      if (declaration.Date) {
        const date = new Date(declaration.Date);

        if (!isNaN(date.getTime())) {
          return date.getTime();
        }

        const parts = declaration.Date.toString().trim().split("-");

        if (parts.length === 3) {
          const [year, month, day] = parts;
          const parsedDate = new Date(`${year}-${month}-${day}T00:00:00`);

          if (!isNaN(parsedDate.getTime())) {
            return parsedDate.getTime();
          }
        }
      }

      return Number(declaration.Id_Declaration || 0);
    };

    const dateDiff = getDeclarationDateValue(b) - getDeclarationDateValue(a);

    if (dateDiff !== 0) {
      return dateDiff;
    }

    return Number(b.Id_Declaration || 0) - Number(a.Id_Declaration || 0);
  });
};

const numberValue = (value) => {
  const num = Number(value || 0);
  return Number.isInteger(num) ? num : num.toFixed(1);
};

const DeclarationsPage = () => {

  const [declarations, setDeclarations] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserName, setSelectedUserName] = useState('');

  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [declarationToDelete, setDeclarationToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [openEditChoiceDialog, setOpenEditChoiceDialog] = useState(false);
  const [editDeclarationData, setEditDeclarationData] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('Tous');

  const navigate = useNavigate();
  const location = useLocation();

  const loadDeclarationsFromDatabase = async (
  userId,
  { showLoading = true, notify = false } = {}
) => {
  if (!userId) return;

  const token = localStorage.getItem("token");

  if (!token) {
    navigate("/login");
    return;
  }

  if (showLoading) {
    setLoading(true);
  }

  try {
    sessionStorage.removeItem(getCacheKey(userId));

    const response = await axios.get(
      `${apiUrl}/declarations/${userId}?fresh=1&t=${Date.now()}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const sortedData = sortDeclarationsRecentFirst(response.data || []);

    setDeclarations(sortedData);

    if (notify) {
      setNotification({
        open: true,
        message: "Données actualisées depuis la base.",
        severity: "success"
      });
    }

  } catch (error) {
    console.error(error);

    setDeclarations([]);

    setNotification({
      open: true,
      message: "Erreur lors du chargement des déclarations.",
      severity: "error"
    });

  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    localStorage.setItem("activeMenu", "declarations");
  }, []);

  useEffect(() => {
  const token = localStorage.getItem("token");

  if (!token) {
    navigate("/login");
    return;
  }

  const decoded = getDecodedToken();

  if (!decoded) {
    localStorage.removeItem("token");
    navigate("/login");
    return;
  }

  const role = decoded.role;
const defaultUserId = decoded.user_id;
const isAdmin = role === "admin";

const userIdToUse = isAdmin
  ? (
      location.state?.userId ||
      location.state?.selectedUserId ||
      localStorage.getItem("selectedUserId") ||
      defaultUserId
    )
  : defaultUserId;

if (!isAdmin) {
  localStorage.removeItem("selectedUserId");
  localStorage.removeItem("selectedUserName");
  sessionStorage.clear();
}

  if (role === "admin" && !userIdToUse) {
    setNotification({
      open: true,
      message: "Aucun salarié sélectionné.",
      severity: "warning"
    });
    setLoading(false);
    return;
  }

  const storedName =
    location.state?.selectedUserName ||
    localStorage.getItem("selectedUserName") ||
    "";

  setSelectedUserId(userIdToUse);
  setSelectedUserName(storedName);

  loadDeclarationsFromDatabase(userIdToUse, { showLoading: true });

}, [
  navigate,
  location.state?.userId,
  location.state?.selectedUserId,
  location.state?.selectedUserName,
  location.state?.refreshKey
]);

  const filteredDeclarations = useMemo(() => {
    let data = [...declarations];

    if (searchTerm.trim() !== '') {
      const term = normalizeText(searchTerm);

      data = data.filter((declaration) => {
        const content = normalizeText(`
          ${getMonthName(declaration)}
          ${formatDateValue(declaration.Date)}
          ${declaration.Jours_Selectionnes || ''}
          ${declaration.jours_congé || ''}
          ${declaration.jours_maladie || ''}
          ${declaration.Somme_Jours || ''}
          ${declaration.Total_Jours_Congé || ''}
          ${declaration.Total_Jours_Maladie || ''}
        `);

        return content.includes(term);
      });
    }

    if (filterType === "travail") {
      data = data.filter((d) => Number(d.Somme_Jours || 0) > 0);
    }

    if (filterType === "conge") {
      data = data.filter((d) => Number(d.Total_Jours_Congé || 0) > 0);
    }

    if (filterType === "maladie") {
      data = data.filter((d) => Number(d.Total_Jours_Maladie || 0) > 0);
    }

    if (filterType === "pdf") {
      data = data.filter((d) => d.url_document_maladie);
    }

    return sortDeclarationsRecentFirst(data);
  }, [declarations, searchTerm, filterType]);

  const totalDeclarations = declarations.length;

  const totalTravail = declarations.reduce(
    (sum, item) => sum + Number(item.Somme_Jours || 0),
    0
  );

  const totalConge = declarations.reduce(
    (sum, item) => sum + Number(item.Total_Jours_Congé || 0),
    0
  );

  const totalMaladie = declarations.reduce(
    (sum, item) => sum + Number(item.Total_Jours_Maladie || 0),
    0
  );

  const totalPDF = declarations.filter((item) => item.url_document_maladie).length;

  const handleDelete = (id) => {
    setDeclarationToDelete(id);
    setOpenDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!declarationToDelete) return;

    setDeleting(true);

    try {
      const token = localStorage.getItem("token");

      await axios.delete(
        `${apiUrl}/declarations/${declarationToDelete}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedDeclarations = declarations.filter(
  d => d.Id_Declaration !== declarationToDelete
);

setDeclarations(updatedDeclarations);

if (selectedUserId) {
  sessionStorage.removeItem(getCacheKey(selectedUserId));

  await loadDeclarationsFromDatabase(selectedUserId, {
    showLoading: false,
    notify: false
  });
}

      setNotification({
        open: true,
        message: "Déclaration supprimée avec succès.",
        severity: "success"
      });

      setOpenDialog(false);
      setDeclarationToDelete(null);

    } catch (error) {
      setNotification({
        open: true,
        message: "Erreur lors de la suppression.",
        severity: "error"
      });

    } finally {
      setDeleting(false);
    }
  };

  const handleAddDeclaration = () => {
    navigate('/Ajouterdeclarations', {
      state: {
        selectedUserId,
        selectedUserName
      }
    });
  };

  const handleAddMaladie = () => {
    navigate('/AjouterMaladie', {
      state: {
        selectedUserId,
        selectedUserName
      }
    });
  };

  const handleEdit = (id, travail, conge, maladie) => {
    setEditDeclarationData({
      declarationId: id,
      joursTravail: travail,
      joursConge: conge,
      joursMaladie: maladie
    });

    setOpenEditChoiceDialog(true);
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterType('Tous');
  };

  const refreshData = async () => {
  if (!selectedUserId) return;

  resetFilters();

  await loadDeclarationsFromDatabase(selectedUserId, {
    showLoading: true,
    notify: true
  });
};

  return (
    <LayoutDashboard>

      <div className="declaration-detail-page">

        <section className="declaration-detail-hero">
          <div>
            <span className="declaration-detail-badge">Déclarations</span>

            <h1>Déclarations des jours de travail</h1>

            <p>
              {selectedUserName
                ? `Suivi détaillé des déclarations de ${selectedUserName}.`
                : "Suivi détaillé des jours travaillés, congés et arrêts maladie."}
            </p>
          </div>
        </section>

        <section className="declaration-detail-kpi-grid">

          <div className="declaration-detail-kpi-card">
            <span>Déclarations</span>
            <strong>{totalDeclarations}</strong>
          </div>

          <div className="declaration-detail-kpi-card">
            <span>Jours travaillés</span>
            <strong>{numberValue(totalTravail)}</strong>
          </div>

          <div className="declaration-detail-kpi-card">
            <span>Jours congé</span>
            <strong className="kpi-orange">{numberValue(totalConge)}</strong>
          </div>

          <div className="declaration-detail-kpi-card">
            <span>Arrêts maladie</span>
            <strong className="kpi-red">{numberValue(totalMaladie)}</strong>
          </div>

        </section>

        <section className="declaration-detail-toolbar-card">

          <div className="declaration-detail-toolbar-header">
            <div>
              <Typography className="declaration-detail-toolbar-title">
                Actions et filtres
              </Typography>

              <Typography className="declaration-detail-toolbar-description">
                Ajoutez, modifiez, recherchez ou filtrez les déclarations affichées.
              </Typography>
            </div>

            <div className="declaration-detail-top-actions">
              <Button
                variant="contained"
                className="declaration-detail-primary-btn"
                onClick={handleAddDeclaration}
              >
                + Ajouter déclaration
              </Button>

              <Button
                variant="contained"
                className="declaration-detail-secondary-btn"
                onClick={handleAddMaladie}
              >
                + Arrêt maladie
              </Button>

              <Button
                variant="outlined"
                className="declaration-detail-refresh-btn"
                onClick={refreshData}
                disabled={loading}
              >
                {loading ? "Chargement..." : "Actualiser"}
              </Button>
            </div>
          </div>

          <div className="declaration-detail-filter-grid">

            <div className="declaration-detail-filter-item declaration-detail-filter-search">
              <Typography className="declaration-detail-filter-label">
                Recherche globale
              </Typography>

              <TextField
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Mois, date, jours..."
                size="small"
                fullWidth
              />
            </div>

            <div className="declaration-detail-filter-item">
              <Typography className="declaration-detail-filter-label">
                Type
              </Typography>

              <TextField
                select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                size="small"
                fullWidth
              >
                <MenuItem value="Tous">Toutes</MenuItem>
                <MenuItem value="travail">Avec travail</MenuItem>
                <MenuItem value="conge">Avec congé</MenuItem>
                <MenuItem value="maladie">Avec maladie</MenuItem>
                <MenuItem value="pdf">Avec PDF</MenuItem>
              </TextField>
            </div>

            <div className="declaration-detail-filter-actions">
              <Button
                variant="outlined"
                className="declaration-detail-reset-btn"
                onClick={resetFilters}
              >
                Réinitialiser
              </Button>
            </div>

          </div>

          <div className="declaration-detail-result-line">
            {filteredDeclarations.length} déclaration(s) affichée(s) sur {declarations.length}
            {totalPDF > 0 ? ` • ${totalPDF} PDF disponible(s)` : ""}
          </div>

        </section>

        <section className="declaration-detail-table-card">

          <TableContainer className="declaration-detail-table" sx={{ overflowX: "auto" }}>
            <Table>

              <TableHead>
                <TableRow>
                  <TableCell>Mois</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Travail</TableCell>
                  <TableCell>Congé</TableCell>
                  <TableCell>Maladie</TableCell>
                  <TableCell>PDF</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>

                {loading && declarations.length === 0 && (
                  <>
                    {[1, 2, 3].map((item) => (
                      <TableRow key={item}>
                        <TableCell colSpan={7}>
                          <div className="declaration-detail-skeleton-line"></div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}

                {!loading && filteredDeclarations.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <div className="declaration-detail-empty-state">
                        Aucune déclaration trouvée.
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {filteredDeclarations.map((declaration) => {
                  const travail = Number(declaration.Somme_Jours || 0);
                  const conge = Number(declaration.Total_Jours_Congé || 0);
                  const maladie = Number(declaration.Total_Jours_Maladie || 0);

                  return (
                    <TableRow key={declaration.Id_Declaration}>

                      <TableCell>
                        <div className="declaration-detail-month-cell">
                          <strong>{getMonthName(declaration)}</strong>
                          <span>ID : {declaration.Id_Declaration}</span>
                        </div>
                      </TableCell>

                      <TableCell>
                        {formatDateValue(declaration.Date)}
                      </TableCell>

                      <TableCell>
                        <span className={travail > 0 ? "declaration-pill pill-work" : "declaration-pill pill-empty"}>
                          {numberValue(travail)}
                        </span>
                      </TableCell>

                      <TableCell>
                        <span className={conge > 0 ? "declaration-pill pill-leave" : "declaration-pill pill-empty"}>
                          {numberValue(conge)}
                        </span>
                      </TableCell>

                      <TableCell>
                        <span className={maladie > 0 ? "declaration-pill pill-sick" : "declaration-pill pill-empty"}>
                          {numberValue(maladie)}
                        </span>
                      </TableCell>

                      <TableCell align="center">
                        {declaration.url_document_maladie ? (
                          <a
                            className="declaration-detail-download-link"
                            href={`${apiUrl}/maladies/download/${declaration.url_document_maladie?.split('/').pop()}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <DownloadIcon />
                          </a>
                        ) : (
                          <span className="declaration-detail-no-pdf">—</span>
                        )}
                      </TableCell>

                      <TableCell align="right">
                        <div className="declaration-detail-actions-cell">

                          <Button
                            variant="outlined"
                            className="declaration-detail-edit-btn"
                            onClick={() =>
                              handleEdit(
                                declaration.Id_Declaration,
                                declaration.Jours_Selectionnes,
                                declaration.jours_congé,
                                declaration.jours_maladie
                              )
                            }
                          >
                            Modifier
                          </Button>

                          <Button
                            variant="outlined"
                            className="declaration-detail-delete-btn"
                            onClick={() => handleDelete(declaration.Id_Declaration)}
                          >
                            Supprimer
                          </Button>

                        </div>
                      </TableCell>

                    </TableRow>
                  );
                })}

              </TableBody>

            </Table>
          </TableContainer>

        </section>

      </div>

      <Dialog
        open={openDialog}
        onClose={() => !deleting && setOpenDialog(false)}
      >
        <DialogTitle>Confirmation</DialogTitle>

        <DialogContent>
          {deleting ? "Suppression en cours..." : "Voulez-vous vraiment supprimer cette déclaration ?"}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpenDialog(false)}
            disabled={deleting}
          >
            Annuler
          </Button>

          <Button
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            {deleting ? "Suppression..." : "Confirmer"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openEditChoiceDialog}
        onClose={() => setOpenEditChoiceDialog(false)}
      >
        <DialogTitle>
          Modifier
        </DialogTitle>

        <DialogContent>
          <Typography>
            Que souhaitez-vous modifier ?
          </Typography>
        </DialogContent>

        <DialogActions>

          <Button
            onClick={() => {
              navigate('/Ajouterdeclarations', {
                state: {
                  declarationId: editDeclarationData.declarationId,
                  selectedUserId,
                  selectedUserName,
                  joursTravail: editDeclarationData.joursTravail,
                  joursConge: editDeclarationData.joursConge,
                  joursMaladie: editDeclarationData.joursMaladie
                }
              });

              setOpenEditChoiceDialog(false);
            }}
          >
            Modifier Déclaration
          </Button>

          <Button
            onClick={() => {
              const maladieBrute = editDeclarationData.joursMaladie;

              if (!maladieBrute || maladieBrute.trim() === "0") {
                setNotification({
                  open: true,
                  message: "Aucun arrêt maladie trouvé pour cette déclaration.",
                  severity: "warning"
                });

                setOpenEditChoiceDialog(false);
                return;
              }

              const maladieArray = maladieBrute
                .split('/')
                .map(d => d.trim())
                .filter(Boolean);

              let selectedMonth = null;
              let selectedYear = null;

              if (maladieArray.length > 0) {
                const firstDate = new Date(maladieArray[0]);

                selectedMonth = firstDate.getMonth() + 1;
                selectedYear = firstDate.getFullYear();
              }

              navigate('/AjouterMaladie', {
                state: {
                  declarationId: editDeclarationData.declarationId,
                  selectedUserId,
                  selectedUserName,
                  selectedMonth,
                  selectedYear
                }
              });

              setOpenEditChoiceDialog(false);
            }}
          >
            Modifier Arrêt Maladie
          </Button>

          <Button onClick={() => setOpenEditChoiceDialog(false)}>
            Annuler
          </Button>

        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={notification.severity}
          sx={{ fontWeight: 'bold' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

    </LayoutDashboard>
  );

};

export default DeclarationsPage;